import { DEFAULT_WRAP_OFFSET_PT } from '@/lib/object-effects/text-wrap'
import { bandsFromCanvasPolygon } from '@/lib/text/shape-contour-wrap'
import { isEffectivelyUnrotated, rotatedRectOffsetCorners } from '@/lib/text/wrap-obstacles'
import type { WrapBand } from '@/lib/text/types/wrap-band'

/**
 * Bounding Box's wrap band(s) for a (possibly rotated) rect. Unrotated shapes take a fast path —
 * one exact rectangle, clamped/edge-snapped exactly as before — since a plain axis-aligned box has
 * no curvature for a scanline to find and slicing it into `CONTOUR_SLICE_HEIGHT_PT`-tall strips
 * would just reproduce the same rectangle in ~30 redundant pieces.
 *
 * A rotated rect does NOT take that shortcut to an axis-aligned box: `rotatedRectOffsetCorners`
 * keeps the true rotated quadrilateral (each side expanded by its own offset in the rect's OWN
 * local frame, then rotated about the rect's real center), and `bandsFromCanvasPolygon` — the same
 * per-row scanline engine Wrap Shape's contour uses — slices THAT polygon. This is what makes every
 * side and every corner of the rotated rectangle participate in the row-by-row wrap test: a row
 * near the rect's narrow tip (e.g. near a 45°-rotated square's top corner) is excluded only across
 * that tip's true (narrow) width, while a row through the rect's wide waist is excluded across its
 * true (wide) width — never a single canvas-aligned rectangle standing in for the whole rotated
 * footprint.
 */
export function computeRotatedBoundingBoxBands(
  rect: { x: number; y: number; width: number; height: number; rotation?: number },
  offsets: {
    wrapOffsetTop?: number
    wrapOffsetRight?: number
    wrapOffsetBottom?: number
    wrapOffsetLeft?: number
  },
  contentX: number,
  contentY: number,
  contentWidth: number,
  contentHeight: number
): WrapBand[] {
  // An absent side must default to the SAME 6pt the panel displays for it — see
  // `DEFAULT_WRAP_OFFSET_PT` — not 0, or a shape can show "6pt" on every side while actually
  // excluding 0pt on whichever sides were never explicitly set (e.g. loaded from a checkpoint
  // that only ever saved some sides).
  const offsetLeft = offsets.wrapOffsetLeft ?? DEFAULT_WRAP_OFFSET_PT
  const offsetRight = offsets.wrapOffsetRight ?? DEFAULT_WRAP_OFFSET_PT
  const offsetTop = offsets.wrapOffsetTop ?? DEFAULT_WRAP_OFFSET_PT
  const offsetBottom = offsets.wrapOffsetBottom ?? DEFAULT_WRAP_OFFSET_PT

  if (isEffectivelyUnrotated(rect.rotation)) {
    const localLeft = rect.x - contentX - offsetLeft
    const localRight = localLeft + rect.width + offsetLeft + offsetRight
    const localTop = rect.y - contentY - offsetTop
    const localBottom = localTop + rect.height + offsetTop + offsetBottom

    const top = Math.max(0, localTop)
    const bottom = Math.min(contentHeight, localBottom)
    const left = Math.max(0, localLeft)
    const right = Math.min(contentWidth, localRight)

    return bottom <= top || right <= left ? [] : [{ top, bottom, left, right }]
  }

  const corners = rotatedRectOffsetCorners(rect, {
    top: offsetTop,
    right: offsetRight,
    bottom: offsetBottom,
    left: offsetLeft,
  })

  // offset: 0 — the four per-side offsets are already baked into `corners`, not a further uniform
  // disk-inflation on top of them.
  return bandsFromCanvasPolygon(corners, 0, contentX, contentY, contentWidth, contentHeight)
}

/** Jump's band, unlike Bounding Box's: spans the object's vertical extent (± top/bottom offset)
 *  but the FULL content width regardless of the object's horizontal position — this is what makes
 *  a row overlapping it have zero free segments (`computeRowFreeSegments`), i.e. text skips the
 *  row entirely rather than squeezing beside the object. Deliberately ignores
 *  `wrapOffsetLeft`/`wrapOffsetRight` — Jump has no left/right text flow to offset (AC 3.2), so
 *  those fields have no geometric effect for this style. */
export function jumpBandFromRect(
  rect: { y: number; height: number },
  offsets: { wrapOffsetTop?: number; wrapOffsetBottom?: number },
  contentY: number,
  contentWidth: number,
  contentHeight: number
): WrapBand | null {
  const offsetTop = offsets.wrapOffsetTop ?? DEFAULT_WRAP_OFFSET_PT
  const offsetBottom = offsets.wrapOffsetBottom ?? DEFAULT_WRAP_OFFSET_PT

  const localTop = rect.y - contentY - offsetTop
  const localBottom = localTop + rect.height + offsetTop + offsetBottom

  const top = Math.max(0, localTop)
  const bottom = Math.min(contentHeight, localBottom)

  if (bottom <= top) return null

  return { top, bottom, left: 0, right: contentWidth }
}
